import { useEffect, useRef, useState } from 'react';

const CHILD_PATH = './reference/exact-index.html';
const MESSAGE_TYPES = new Set([
  'nbs-resize',
  'nbs-scroll-top',
  'nbs-upload-url-request',
  'nbs-submit',
]);
const WIX_SUBMIT_ENDPOINT = 'https://www.nina-b-studio.com/_functions/nbsSubmit';
const WIX_UPLOAD_ENDPOINT = 'https://www.nina-b-studio.com/_functions/nbsUploadUrl';
const EMBED_ORIGINS = new Set([
  'https://www.nina-b-studio.com',
  'https://nina-b-studio.com',
  'https://editor.wix.com',
]);

function parentOrigin() {
  if (window.parent === window || !document.referrer) return null;
  try {
    const origin = new URL(document.referrer).origin;
    return EMBED_ORIGINS.has(origin) || origin.endsWith('.editor.wix.com') || origin.endsWith('.wixsite.com') ? origin : null;
  } catch { return null; }
}

/**
 * Enveloppe standalone React.
 *
 * Le document de référence validé est servi sans réinterprétation dans
 * l'iframe. Les textes, le DOM, le CSS, les animations et la logique restent
 * donc strictement ceux du configurateur Wix audité. L'enveloppe ne gère que
 * la hauteur et le relais vers Wix lorsqu'elle est elle-même embarquée.
 */
export function App() {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height,setHeight] = useState(820);
  const [screen,setScreen] = useState('intro');

  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow) return;
      const data = event.data ?? {};
      if (!MESSAGE_TYPES.has(data.type)) return;
      if (data.type === 'nbs-scroll-top') {
        const resetScroll = () => {
          window.scrollTo({top:0,left:0,behavior:'auto'});
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        };
        resetScroll();
        requestAnimationFrame(() => {
          resetScroll();
          requestAnimationFrame(resetScroll);
        });
        const origin = parentOrigin();
        if (origin) window.parent.postMessage(data, origin);
        return;
      }
      if (data.type === 'nbs-resize') {
        const nextScreen = String(data.screen || '');
        setScreen(nextScreen);
        if (nextScreen === 'intro') return;
        const minimumHeight = nextScreen === 'success' ? 480 : 620;
        setHeight(Math.max(minimumHeight, Math.min(14000, Number(data.height) || 820)));
        return;
      }
      // Lorsque le configurateur est intégré dans Wix, la page parente garde
      // le contrat historique. En standalone, l'enveloppe enregistre la
      // demande via l'API publique du site Wix, qui déclenche les automations.
      const origin = parentOrigin();
      if (origin) {
        window.parent.postMessage(data, origin);
        return;
      }
      if (data.type === 'nbs-upload-url-request') {
        try {
          const response = await fetch(WIX_UPLOAD_ENDPOINT, {
            method: 'POST',
            headers: {'Content-Type': 'text/plain;charset=UTF-8'},
            body: JSON.stringify({fileName:data.fileName,mimeType:data.mimeType}),
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok || !result.uploadUrl) throw new Error(result.message || 'Téléversement indisponible.');
          frame.current?.contentWindow?.postMessage({
            type: 'nbs-upload-url-result',
            requestId: data.requestId,
            uploadUrl: result.uploadUrl,
          }, location.origin);
        } catch (error) {
          frame.current?.contentWindow?.postMessage({
            type: 'nbs-upload-url-result',
            requestId: data.requestId,
            error: error instanceof Error ? error.message : 'Le téléversement est indisponible.',
          }, location.origin);
        }
        return;
      }
      if (data.type !== 'nbs-submit') return;
      try {
        const response = await fetch(WIX_SUBMIT_ENDPOINT, {
          method: 'POST',
          headers: {'Content-Type': 'text/plain;charset=UTF-8'},
          body: JSON.stringify(data.payload ?? {}),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) throw new Error(result.message || 'Envoi impossible.');
        frame.current?.contentWindow?.postMessage({type:'nbs-submit-success', payload:result}, location.origin);
      } catch (error) {
        frame.current?.contentWindow?.postMessage({
          type: 'nbs-submit-error',
          message: error instanceof Error ? error.message : 'La demande n’a pas pu être transmise.',
        }, location.origin);
      }
    };
    addEventListener('message', onMessage);
    return () => removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const onParentMessage = (event: MessageEvent) => {
      if (event.source !== parent || parent === window || event.origin !== parentOrigin()) return;
      if (!['nbs-upload-url-result','nbs-submit-success','nbs-submit-error'].includes(event.data?.type)) return;
      frame.current?.contentWindow?.postMessage(event.data, location.origin);
    };
    addEventListener('message', onParentMessage);
    return () => removeEventListener('message', onParentMessage);
  }, []);

  return <iframe ref={frame} className="reference-frame" src={CHILD_PATH} title="Configurateur de projet Nina B Studio" style={{height:screen === 'intro' ? '100dvh' : `${height}px`}}/>;
}
